// TelcoTrack — reusable ROZVADEC weld-count semantics (web / mobile / backend).
// Primary = unique incoming cable/fiber pairs. Secondary = assigned branches,
// plus unassigned DIRECT rows only on technology types (OS / SDF / RSU).

import { secondaryWeldCountForCabinetType } from './rozvadec-cabinet.constants';

export type RozvadecWeldCountRow = {
  kind: string;
  buildingId?: string | null;
  inCableId?: string | null;
  inPhysicalFiber?: number | null;
};

export type RozvadecWeldBillingSource = 'physical-weld' | 'legacy-manual';

export type RozvadecDerivedWeldCounts = {
  directCount: number;
  connectorCount: number;
  splitterOutputCount: number;
  assignedBranchCount: number;
  unassignedDirectCount: number;
  weldCountPrimary: number;
  weldCountSecondary: number;
  /** Unique stored physical welds. Price from this, not primary+secondary. */
  weldCountTotal: number;
  weldBillingSource: RozvadecWeldBillingSource;
};

export function isCompleteCableFiberPair(
  cableId: unknown,
  physicalFiber: unknown,
): boolean {
  const cable = typeof cableId === 'string' ? cableId.trim() : '';
  return (
    cable.length > 0 &&
    typeof physicalFiber === 'number' &&
    Number.isInteger(physicalFiber) &&
    physicalFiber >= 1
  );
}

export function rozvadecFiberHopKey(cableId: string, physicalFiber: number): string {
  return `${cableId.trim()}#${physicalFiber}`;
}

/** Distinct incoming `cableId#physicalFiber` keys — primary weld count. */
export function uniqueIncomingCableFiberKeys(
  rows: readonly RozvadecWeldCountRow[],
): string[] {
  const incoming = new Set<string>();
  for (const row of rows) {
    if (!isCompleteCableFiberPair(row.inCableId, row.inPhysicalFiber)) continue;
    incoming.add(rozvadecFiberHopKey(row.inCableId as string, row.inPhysicalFiber as number));
  }
  return [...incoming];
}

export function countAssignedBranches(rows: readonly RozvadecWeldCountRow[]): number {
  return rows.filter((row) => Boolean(String(row.buildingId ?? '').trim())).length;
}

export function countUnassignedDirects(rows: readonly RozvadecWeldCountRow[]): number {
  return rows.filter(
    (row) => row.kind === 'direct' && !String(row.buildingId ?? '').trim(),
  ).length;
}

/**
 * Server / client SSOT for milestone weld counts.
 * Primary never counts splitter fan-out rows separately.
 * When no incoming pairs exist: historical directs-only rows count as primary;
 * building-only rows do not.
 */
export function deriveRozvadecWeldCounts(
  connections: readonly RozvadecWeldCountRow[],
  options?: { typeId?: string | null },
): RozvadecDerivedWeldCounts {
  let directCount = 0;
  let connectorCount = 0;
  let splitterOutputCount = 0;
  let assignedBranchCount = 0;
  let unassignedDirectCount = 0;
  const incoming = new Set<string>();
  for (const row of connections) {
    const hasBuilding = Boolean(String(row.buildingId ?? '').trim());
    if (row.kind === 'direct') {
      directCount += 1;
      if (!hasBuilding) unassignedDirectCount += 1;
    } else if (row.kind === 'connector') {
      connectorCount += 1;
    } else if (row.kind === 'splitter') {
      splitterOutputCount += 1;
    }
    if (hasBuilding) assignedBranchCount += 1;
    if (isCompleteCableFiberPair(row.inCableId, row.inPhysicalFiber)) {
      incoming.add(rozvadecFiberHopKey(row.inCableId as string, row.inPhysicalFiber as number));
    }
  }
  return {
    directCount,
    connectorCount,
    splitterOutputCount,
    assignedBranchCount,
    unassignedDirectCount,
    weldCountPrimary:
      incoming.size > 0 ? incoming.size : assignedBranchCount === 0 ? directCount : 0,
    weldCountSecondary: secondaryWeldCountForCabinetType(
      options?.typeId,
      assignedBranchCount,
      unassignedDirectCount,
    ),
    weldCountTotal: 0,
    weldBillingSource: 'legacy-manual',
  };
}
